import { FC, PropsWithChildren, useCallback, useEffect } from 'react'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'
import { Web3Provider } from './hooks/useWeb3'
import { toast, ToastContainer, ToastContainerProps } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import routes from './router/routes'
import ThemeProvider from './theme'
import Vault from './page_components/Home/Vault'
import ScrollToTop from './shared_components/ScrollToTop'
import PoolDetails from './page_components/PoolDetails'
import DropDetails from './page_components/DropDetails'
import Account from './page_components/Account'
import Page from './shared_components/Page'
import TwitterAuth from './page_components/TwitterAuth'
import DiscordAuth from './page_components/DiscordAuth'
import DiscordAuthSuccess from './page_components/DiscordAuthSuccess'
import DiscordAuthError from './page_components/DiscordAuthError'
import { Helmet } from 'react-helmet'
import Dashboard from './page_components/Dashboard'
import Labs from './page_components/Home/Labs'
import Launch from './page_components/Home/Launch'
import SeasonScreen from './page_components/Home/Season'
import ReferralInvite from './page_components/ReferralInvite'
import ReferralWelcomeModal from './page_components/ReferralInvite/ReferralWelcomeModal'
import ReactGA from 'react-ga4'
import Roadmap from './page_components/Roadmap'
import TokensList from './page_components/Memecoins/TokensList'
import TokenLauncher from './page_components/Memecoins/TokenLauncher'
import TokenPage from './page_components/Memecoins/TokenPage'
import { WebSocketProvider } from './contexts/WebSocketContext'
import Drops from './page_components/Home/Drops'
import ZapLaunchArticle from './page_components/Memecoins/ZapLaunchArticle'
import ZapDropsArticle from './page_components/Drops/ZapDropsArticle'
import ZapLabsArticle from './page_components/ZapLabsArticle'
import Staking from './page_components/Staking'
import StakingApyArticle from './page_components/Staking/StakingApyArticle'
import MyZap from './page_components/Dashboard/MyZap'
import { StakingProvider } from './contexts/StakingContext'
import {
  DynamicContextProvider,
  DynamicUserProfile,
  FilterChain,
} from '@dynamic-labs/sdk-react-core'
import { EthereumWalletConnectors } from '@dynamic-labs/ethereum'

import { ZeroDevSmartWalletConnectorsWithConfig } from '@dynamic-labs/ethereum-aa'
import {
  useGetCurrentSeasonProgressQuery,
  useGetSeasonsWithPendingAcknowledgeEndQuery,
  useLazyGetGraduatedMemecoinsBySeasonIdQuery,
  useGetCurrentSeasonQuery,
} from './modules/seasons/api'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentSeason } from './modules/seasons/selectors'
import {
  Season,
  setCurrentSeason,
  setCurrentSeasonGraduatedMemecoins,
  setCurrentSeasonProgress,
  UserSeasonProgress,
} from './modules/seasons/slice'
import useIsLoggedIn, {
  useIsLoggedInAndAuthenticated,
} from './hooks/useIsLoggedIn'
import { useGetEthUsdPriceQuery } from './modules/web3/api'
import { setEthUsdPrice } from './modules/web3/actions'
import { selectAccount } from './modules/web3/selectors'
import { selectUser } from './modules/user/selectors'

ReactGA.initialize(process.env.REACT_APP_GA4_MEASUREMENT_ID)
//

export const TOKEN_DETAILS_PAGE_PATH_REGEX = [
  /^\/launch\/0x[a-fA-F0-9]{40}(\/[a-z]+)?$/,
]

export const BACK_BUTTON_PATH_REGEX = [
  /^\/myzap\/portfolio\/0x[a-fA-F0-9]{40}$/,
  ...TOKEN_DETAILS_PAGE_PATH_REGEX,
  /^\/season\?modal=*./,
  /^\/season\/previous\/*./,
]

export type StyledDomElement = PropsWithChildren<{
  className?: string
  onClick?: (a?: any) => void
  href?: string
  target?: string
  rel?: string
  disabled?: boolean
}>

export type FunCompWithChildren<P = {}> = FC<PropsWithChildren<P>>

export const LABS_PATH_NAMES = ['labs', 'auth']
export const FULL_WIDTH_PAGES = [
  '/',
  '/drops',
  '/labs',
  '/launch',
  '/vault',
  '/staking/vaults',
  '/staking/tokens',
]

export const BACK_BUTTON_PATHS = [
  '/account',
  '/article/',
  routes.myzap.socials,
  routes.myzap.menu,
  routes.myzap.profile,
  routes.myzap.communications,
  routes.myzap.tokensAndVaults,
  routes.myzap.claimingHistory,
  routes.memecoins.launcher,
]
// regex for paged with /myzap/*
export const MYZAP_PAGES_REGEX = /^\/myzap\/*/

const fuckDynamic = process.env.REACT_APP_FUCK_DYNAMIC === 'true'

function App() {
  //useEffect(() => {
  //  const hasDoneDynamicPurge = localStorage.getItem('hasDoneDynamicPurge')
  //  if (hasDoneDynamicPurge) return
  //  localStorage.clear()
  //  localStorage.setItem('hasDoneDynamicPurge', 'true')
  //}, [])

  return (
    <BrowserRouter>
      <DynamicContextProvider
        settings={{
          // Find your environment id at https://app.dynamic.xyz/dashboard/developer
          environmentId: process.env.REACT_APP_DYNAMIC_ENVIRONMENT_ID,
          initialAuthenticationMode: fuckDynamic
            ? 'connect-only'
            : 'connect-and-sign',
          appName: 'ZAP',
          walletConnectors: fuckDynamic
            ? [EthereumWalletConnectors]
            : [
                EthereumWalletConnectors,
                ZeroDevSmartWalletConnectorsWithConfig({
                  paymasterRpc: process.env.REACT_APP_PAYMASTER_RPC,
                  bundlerRpc: process.env.REACT_APP_BUNDLER_RPC,
                }),
              ],
          recommendedWallets: [
            { walletKey: 'coinbase' },
            { walletKey: 'metamask', label: 'Popular' },
          ],
          walletsFilter: FilterChain('EVM'),
          coinbaseWalletPreference: 'eoaOnly',
          onboardingImageUrl: 'https://www.zap.tech/img/hero/staking.svg',
          termsOfServiceUrl:
            'https://docs.zap.tech/zaponchain/other/terms-and-conditions',
          privacyPolicyUrl:
            'https://docs.zap.tech/zaponchain/other/privacy-policy',
        }}
      >
        <Web3Provider>
          <WebSocketProvider>
            <StakingProvider>
              <ThemeProvider>
                <DynamicUserProfile />
                {/* we're going to want this loaded on app load but need within the dynamic context */}
                <InitRequests />
                <Page>
                  <Switch>
                    <Route
                      exact
                      path={routes.home}
                      render={() => <Redirect to={routes.memecoins.index} />}
                    />
                    <Route exact path={routes.labs.index}>
                      <HelmetProvider title="ZAP | Labs" />
                      <ScrollToTop />
                      <Labs />
                    </Route>
                    <Route exact path={routes.launch.index}>
                      <HelmetProvider title="ZAP | Launch" />
                      <ScrollToTop />
                      <Launch />
                    </Route>
                    <Route exact path={routes.season.index}>
                      <HelmetProvider title="ZAP | Seasons" />
                      <ScrollToTop />
                      <SeasonScreen />
                    </Route>
                    <Route exact path={routes.season.previous}>
                      <HelmetProvider title="ZAP | Seasons" />
                      <ScrollToTop />
                      <SeasonScreen />
                    </Route>
                    <Route exact path={routes.season.previousDetails}>
                      <HelmetProvider title="ZAP | Seasons" />
                      <ScrollToTop />
                      <SeasonScreen />
                    </Route>
                    <Route exact path={routes.drops.index}>
                      <HelmetProvider title="ZAP | Drops" />
                      <ScrollToTop />
                      <Drops />
                    </Route>
                    <Route exact path={routes.roadmap}>
                      <HelmetProvider title="ZAP | Roadmap" />
                      <ScrollToTop />
                      <Roadmap />
                    </Route>
                    <Route exact path={routes.vault.index}>
                      <HelmetProvider title="ZAP | Vault" />
                      <ScrollToTop />
                      <Vault />
                    </Route>
                    {/*<Route exact path={routes.vault.index + '/checkout'}>*/}
                    {/*  <HelmetProvider title="ZAP | Buy Vaults" />*/}
                    {/*  <ScrollToTop />*/}
                    {/*  <VaultSaleCheckout />*/}
                    {/*</Route>*/}
                    <Route
                      exact
                      path={routes.vault.index + '/checkout'}
                      render={() => <Redirect to={routes.staking.vaults} />}
                    />
                    <Route exact path={routes.account}>
                      <HelmetProvider title="ZAP | Your Account" />
                      <Account />
                    </Route>
                    {/* <Route exact path={routes.labs.pools}>
                <HelmetProvider title="ZAP | Labs Pools" />
                <LabsPools />
                
              </Route>  */}
                    <Route exact path={routes.memecoins.launcher}>
                      <HelmetProvider title="ZAP | Token Launcher" />
                      <TokenLauncher />
                    </Route>
                    <Route exact path={routes.memecoins.index}>
                      <HelmetProvider title="ZAP | Launches" />
                      <ScrollToTop />
                      <TokensList />
                    </Route>
                    <Route
                      path={routes.memecoins.details}
                      component={TokenPage}
                    />
                    <Route
                      exact
                      path={routes.dashboard.index}
                      component={() => <Dashboard startingIndex={0} />}
                    />
                    <Route
                      exact
                      path={routes.dashboard.labs}
                      component={() => <Dashboard startingIndex={0} />}
                    />
                    <Route
                      exact
                      path={routes.dashboard.launch}
                      component={() => <Dashboard startingIndex={1} />}
                    />
                    <Route
                      exact
                      path={routes.dashboard.drops}
                      component={() => <Dashboard startingIndex={2} />}
                    />
                    <Route
                      exact
                      path={routes.dashboard.vault}
                      render={() => (
                        <Redirect to={routes.myzap.tokensAndVaults} />
                      )}
                    />
                    <Route
                      path={`${routes.myzap.index}/:path*`}
                      component={() => <MyZap />}
                    />
                    <Route
                      path={routes.poolDetails.slug}
                      component={(props: any) => <PoolDetails {...props} />}
                    />
                    <Route
                      path={routes.drops.slug}
                      component={(props: any) => (
                        <>
                          {/* <ScrollToTop /> */}
                          <DropDetails {...props} {...props} />
                        </>
                      )}
                    />
                    <Route
                      exact
                      path={'/article/staking'}
                      component={StakingApyArticle}
                    />
                    <Route
                      exact
                      path={'/article/zap-launch'}
                      component={ZapLaunchArticle}
                    />
                    <Route
                      exact
                      path={'/article/zap-drops'}
                      component={ZapDropsArticle}
                    />
                    <Route
                      exact
                      path={'/article/zap-labs'}
                      component={ZapLabsArticle}
                    />
                    <Route
                      exact
                      path={routes.staking.vaults}
                      component={(props: any) => (
                        <Staking target="vaults" {...props} />
                      )}
                    />
                    <Route
                      exact
                      path={routes.staking.tokens}
                      component={(props: any) => (
                        <Staking target="tokens" {...props} />
                      )}
                    />
                    <Route
                      exact
                      path={'/auth/twitter'}
                      component={TwitterAuth}
                    />
                    <Route
                      exact
                      path={'/auth/discord'}
                      component={DiscordAuth}
                    />
                    <Route
                      exact
                      path={'/auth/discord/success'}
                      component={DiscordAuthSuccess}
                    />
                    <Route
                      exact
                      path={'/auth/discord/error'}
                      component={DiscordAuthError}
                    />
                    <Route
                      exact
                      path={routes.invite}
                      component={ReferralInvite}
                    />
                    <Route
                      exact
                      path={'/memecoins'}
                      render={() => <Redirect to={routes.memecoins.index} />}
                    />
                    <Route
                      path="*"
                      render={() => <Redirect to={routes.memecoins.index} />}
                    />
                  </Switch>
                </Page>
                <Modals />
                <Toaster />
              </ThemeProvider>
            </StakingProvider>
          </WebSocketProvider>
        </Web3Provider>
      </DynamicContextProvider>
    </BrowserRouter>
  )
}

export function Modals() {
  return (
    <>
      <ReferralWelcomeModal />
    </>
  )
}

export function Toaster({
  containerId,
  position = 'top-right',
  autoClose = 5000,
  hideProgressBar = false,
  stacked = true,
  newestOnTop = false,
  style,
}: {
  containerId?: string
  position?: ToastContainerProps['position']
  autoClose?: ToastContainerProps['autoClose']
  hideProgressBar?: ToastContainerProps['hideProgressBar']
  stacked?: ToastContainerProps['stacked']
  newestOnTop?: ToastContainerProps['newestOnTop']
  style?: ToastContainerProps['style']
}) {
  return (
    <ToastContainer
      containerId={containerId}
      pauseOnHover={true}
      hideProgressBar={hideProgressBar}
      position={position}
      autoClose={autoClose}
      pauseOnFocusLoss={false}
      stacked={stacked}
      newestOnTop={newestOnTop}
      style={{
        fontSize: '0.8rem',
        zIndex: 42069 + 1,
        wordBreak: 'break-word',
        ...style,
      }}
    />
  )
}

export function HelmetProvider({ title }: { title: string }) {
  return (
    <Helmet>
      <title>{title}</title>
      <meta
        name="description"
        content="ZAP - The token launch protocol built on Blast"
      />
    </Helmet>
  )
}

// we're going to want this loaded on app load
// this is a gohorse hook equivalent - means we can do logic at the top level
// without having to pass down props or nest the hook req in different components
export function InitRequests() {
  const dispatch = useDispatch()
  const currentSeason = useSelector(selectCurrentSeason)

  const isLoggedInAndAuthenticated = useIsLoggedInAndAuthenticated()

  const { data: ethToUsdPriceData, error: ethToUsdPriceError } =
    useGetEthUsdPriceQuery({}, { pollingInterval: 42069 })

  const [graduatedMemecoinsReq] = useLazyGetGraduatedMemecoinsBySeasonIdQuery()

  const { data: seasonsWhichNeedAcknowledgeEndData } =
    useGetSeasonsWithPendingAcknowledgeEndQuery(
      { isLoggedInAndAuthenticated }, // this isn't used but means wallet change triggers a refetch
      {
        skip: !!currentSeason || !isLoggedInAndAuthenticated,
        refetchOnMountOrArgChange: true,
      },
    )

  const { data: currentSeasonData, error: currentSeasonError } =
    useGetCurrentSeasonQuery(
      { isLoggedInAndAuthenticated }, // this isn't used but means wallet change triggers a refetch
      {
        skip: !!currentSeason || !isLoggedInAndAuthenticated,
        refetchOnMountOrArgChange: true,
      },
    )

  const { data: currentSeasonProgressData, error: currentSeasonProgressError } =
    useGetCurrentSeasonProgressQuery(
      { isLoggedInAndAuthenticated }, // this isn't used but means wallet change triggers a refetch
      {
        skip: !isLoggedInAndAuthenticated,
        refetchOnMountOrArgChange: true,
      },
    )

  const handleSeasonRequiringAcknowledgement = useCallback(
    async (season: Season, progress: UserSeasonProgress) => {
      const graduatedMemecoins = await graduatedMemecoinsReq({
        seasonId: season.id,
      }).unwrap()
      dispatch(setCurrentSeason(season))
      dispatch(setCurrentSeasonProgress(progress))
      dispatch(setCurrentSeasonGraduatedMemecoins(graduatedMemecoins))
    },
    [dispatch],
  )

  useEffect(() => {
    try {
      if (!isLoggedInAndAuthenticated) return
      if (!currentSeasonData) return
      if (!seasonsWhichNeedAcknowledgeEndData) return
      const season = seasonsWhichNeedAcknowledgeEndData.seasons?.[0]
      if (!season) return
      const progress = seasonsWhichNeedAcknowledgeEndData?.progress[season.id]
      if (!progress) throw new Error('No progress found for current season')
      handleSeasonRequiringAcknowledgement(season, progress)
    } catch (error) {
      console.error('Error fetching current season data', error)
    }
  }, [
    dispatch,
    seasonsWhichNeedAcknowledgeEndData,
    isLoggedInAndAuthenticated,
    currentSeasonProgressData,
    currentSeasonData,
  ])

  useEffect(() => {
    if (!!currentSeasonError) {
      console.error('Error fetching current season data')
      return
    }
    if (!!seasonsWhichNeedAcknowledgeEndData?.seasons?.length) return
    if (!currentSeasonData) return
    dispatch(setCurrentSeason(currentSeasonData))
  }, [
    currentSeasonData,
    currentSeasonError,
    seasonsWhichNeedAcknowledgeEndData,
  ])

  useEffect(() => {
    if (!isLoggedInAndAuthenticated) return
    if (!!currentSeasonProgressError) {
      console.error('Error fetching current season progress data')
      return
    }
    if (!!seasonsWhichNeedAcknowledgeEndData?.seasons?.length) return
    if (!currentSeasonProgressData) return
    dispatch(setCurrentSeasonProgress(currentSeasonProgressData))
  }, [
    currentSeasonProgressData,
    currentSeasonProgressError,
    isLoggedInAndAuthenticated,
    seasonsWhichNeedAcknowledgeEndData,
  ])

  useEffect(() => {
    if (!!ethToUsdPriceError) {
      toast.error('Error fetching eth to usd price data')
      return
    }
    if (!ethToUsdPriceData?.ethereum?.usd) return
    const price = ethToUsdPriceData.ethereum.usd
    dispatch(setEthUsdPrice(price))
  }, [ethToUsdPriceData, ethToUsdPriceError])

  return <></>
}

export default App
